import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import api from "../../services/api";
import AdminLogoHeader from "../../components/admin/AdminLogoHeader";

const formatDateTime = (value) => {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString();
};

const formatCurrency = (amount) => {
  const numeric = Number(amount || 0);
  return new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: numeric % 1 === 0 ? 0 : 2,
  }).format(numeric);
};

const ComplianceCRMDetail = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const loadDetail = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError("");
      const data = await api.adminGetApprovedUserDetail(userId);
      setDetail(data);
      setForm({
        first_name: data.first_name || "",
        middle_name: data.middle_name || "",
        last_name: data.last_name || "",
        phone_number: data.phone_number || "",
        date_of_birth: data.date_of_birth || "",
        admin_note: data.admin_note || "",
        bank_name: data.bank_name || "",
        bank_account_name: data.bank_account_name || "",
        bank_account_number: data.bank_account_number || "",
        bvn: data.bvn || "",
        is_verified: Boolean(data.is_verified),
        kyc_approved: Boolean(data.kyc_approved),
      });
    } catch (err) {
      console.error("approved user detail", err);
      setError(err.message || "Unable to load client record.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const fullName = useMemo(() => {
    if (!detail) return "";
    return (
      detail.full_name ||
      [detail.first_name, detail.middle_name, detail.last_name].filter(Boolean).join(" ") ||
      ""
    );
  }, [detail]);

  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const handleReset = () => {
    if (!detail) return;
    setStatus("");
    setForm({
      first_name: detail.first_name || "",
      middle_name: detail.middle_name || "",
      last_name: detail.last_name || "",
      phone_number: detail.phone_number || "",
      date_of_birth: detail.date_of_birth || "",
      admin_note: detail.admin_note || "",
      bank_name: detail.bank_name || "",
      bank_account_name: detail.bank_account_name || "",
      bank_account_number: detail.bank_account_number || "",
      bvn: detail.bvn || "",
      is_verified: Boolean(detail.is_verified),
      kyc_approved: Boolean(detail.kyc_approved),
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form || !userId) return;
    try {
      setSaving(true);
      setStatus("");
      const payload = {
        first_name: form.first_name || null,
        middle_name: form.middle_name || null,
        last_name: form.last_name || null,
        phone_number: form.phone_number || null,
        date_of_birth: form.date_of_birth || null,
        admin_note: form.admin_note ?? "",
        bank_name: form.bank_name || null,
        bank_account_name: form.bank_account_name || null,
        bank_account_number: form.bank_account_number || null,
        bvn: form.bvn || null,
        is_verified: Boolean(form.is_verified),
        kyc_approved: Boolean(form.kyc_approved),
      };
      const updated = await api.adminUpdateApprovedUser(userId, payload);
      setDetail(updated);
      setForm({
        first_name: updated.first_name || "",
        middle_name: updated.middle_name || "",
        last_name: updated.last_name || "",
        phone_number: updated.phone_number || "",
        date_of_birth: updated.date_of_birth || "",
        admin_note: updated.admin_note || "",
        bank_name: updated.bank_name || "",
        bank_account_name: updated.bank_account_name || "",
        bank_account_number: updated.bank_account_number || "",
        bvn: updated.bvn || "",
        is_verified: Boolean(updated.is_verified),
        kyc_approved: Boolean(updated.kyc_approved),
      });
      setStatus("Client record updated successfully.");
    } catch (err) {
      console.error("update approved user", err);
      setError(err.message || "Unable to update client record.");
    } finally {
      setSaving(false);
    }
  };

  const documents = detail?.documents || [];

  const handleUpload = async (event) => {
    if (!userId) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const docType = window.prompt("Enter document type label (e.g., ID, Utility bill, Passport):", "additional");
    if (docType === null) return; // cancelled
    const trimmed = docType.trim();
    if (!trimmed) {
      setUploadError("Document type is required.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", trimmed);
    try {
      setUploadError("");
      setUploading(true);
      await api.adminUploadApprovedUserDocument(userId, formData);
      await loadDetail();
    } catch (err) {
      console.error("upload document", err);
      setUploadError(err.message || "Upload failed");
    } finally {
      setUploading(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <AdminLogoHeader />
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-emerald-600 hover:underline"
        >
          &larr; Back to CRM
        </button>

        <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
          <div className="border-b border-slate-100 px-6 py-4">
            <h1 className="text-xl font-semibold text-slate-900">Client profile</h1>
            <p className="text-sm text-slate-500">
              View and maintain customer information, banking data, and uploaded compliance documents.
            </p>
          </div>

          {loading ? (
            <p className="px-6 py-6 text-sm text-slate-500">Loading client profile...</p>
          ) : error ? (
            <p className="px-6 py-6 text-sm text-red-600">{error}</p>
          ) : !detail ? (
            <p className="px-6 py-6 text-sm text-slate-500">Client not found.</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6 px-6 py-6">
              {status && (
                <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {status}
                </p>
              )}

              <div className="grid gap-6 md:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4 md:col-span-3">
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">{fullName || detail.username}</h2>
                      <p className="text-sm text-slate-500">{detail.username}</p>
                    </div>
                    <div className="text-sm text-slate-600">
                      <div>Role: <span className="font-medium capitalize">{detail.role || "user"}</span></div>
                      <div>Wallet balance: <span className="font-medium">{formatCurrency(detail.wallet_balance)}</span></div>
                      <div>Created at: <span className="font-medium">{formatDateTime(detail.created_at)}</span></div>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2 space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Personal details</h3>
                  <div className="grid gap-4 md:grid-cols-2">
                    <label className="flex flex-col gap-1 text-sm text-slate-600">
                      First name
                      <input
                        type="text"
                        value={form?.first_name || ""}
                        onChange={handleFieldChange("first_name")}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm text-slate-600">
                      Middle name
                      <input
                        type="text"
                        value={form?.middle_name || ""}
                        onChange={handleFieldChange("middle_name")}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm text-slate-600">
                      Last name
                      <input
                        type="text"
                        value={form?.last_name || ""}
                        onChange={handleFieldChange("last_name")}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm text-slate-600">
                      Phone number
                      <input
                        type="text"
                        value={form?.phone_number || ""}
                        onChange={handleFieldChange("phone_number")}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-sm text-slate-600">
                      Date of birth
                      <input
                        type="date"
                        value={form?.date_of_birth || ""}
                        onChange={handleFieldChange("date_of_birth")}
                        className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                      />
                    </label>
                    <div className="rounded-lg border border-slate-200 px-3 py-3 text-sm text-slate-600">
                      <label className="flex items-center justify-between">
                        <span>Email/username</span>
                        <span className="font-medium text-slate-900">{detail.username}</span>
                      </label>
                      <label className="mt-3 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(form?.is_verified)}
                          onChange={handleFieldChange("is_verified")}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-slate-700">Account verified</span>
                      </label>
                      <label className="mt-2 flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Boolean(form?.kyc_approved)}
                          onChange={handleFieldChange("kyc_approved")}
                          className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-slate-700">KYC approved</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Banking details</h3>
                  <label className="flex flex-col gap-1 text-sm text-slate-600">
                    Bank name
                    <input
                      type="text"
                      value={form?.bank_name || ""}
                      onChange={handleFieldChange("bank_name")}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-600">
                    Account name
                    <input
                      type="text"
                      value={form?.bank_account_name || ""}
                      onChange={handleFieldChange("bank_account_name")}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-600">
                    Account number
                    <input
                      type="text"
                      value={form?.bank_account_number || ""}
                      onChange={handleFieldChange("bank_account_number")}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-sm text-slate-600">
                    BVN
                    <input
                      type="text"
                      value={form?.bvn || ""}
                      onChange={handleFieldChange("bvn")}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    />
                  </label>
                </div>

                <div className="md:col-span-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Admin note</h3>
                  <textarea
                    rows={5}
                    value={form?.admin_note || ""}
                    onChange={handleFieldChange("admin_note")}
                    className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-200"
                    placeholder="Add internal notes or reminders for the CRM team."
                  />
                </div>
              </div>

              <div className="flex flex-col gap-3 border-t border-slate-100 pt-4 md:flex-row md:justify-end">
                <button
                  type="button"
                  onClick={handleReset}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                  disabled={saving}
                >
                  Reset
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save changes"}
                </button>
              </div>
            </form>
          )}
        </section>

        {detail && !loading && !error && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100">
            <div className="border-b border-slate-100 px-6 py-4">
              <h2 className="text-lg font-semibold text-slate-900">Compliance documents</h2>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-slate-500">All files uploaded by the client after onboarding.</p>
                <div className="flex items-center gap-2">
                  <label className="inline-flex cursor-pointer items-center rounded-lg border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50">
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleUpload}
                      disabled={uploading}
                      accept="image/*,.pdf,.doc,.docx,.txt"
                    />
                    {uploading ? "Uploading..." : "Attach document"}
                  </label>
                </div>
              </div>
              {uploadError && (
                <p className="mt-2 text-sm text-red-600">
                  {uploadError}
                </p>
              )}
            </div>
            <div className="px-6 py-6">
              {!documents.length ? (
                <p className="text-sm text-slate-500">No documents on file for this client.</p>
              ) : (
                <ul className="space-y-3 text-sm text-slate-700">
                  {documents.map((doc) => (
                    <li key={doc.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900 capitalize">{doc.doc_type}</p>
                        <p className="text-xs text-slate-500">
                          Uploaded {formatDateTime(doc.uploaded_at)} · Status: {doc.status || "pending"}
                        </p>
                      </div>
                      {doc.file_url ? (
                        <a
                          href={doc.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="rounded-lg border border-emerald-200 px-3 py-2 text-sm text-emerald-600 hover:bg-emerald-50"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">No link available</span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ComplianceCRMDetail;
